import { useCallback, useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Loader2, RefreshCw } from "lucide-react"
import { ArmResultsTable } from "@/features/outreach/components/ArmResultsTable"
import * as programApi from "../../api/outreach-program.api"
import { apiErrorMessage, describeCounts, formatCentral } from "../../lib/program"
import type { ByReason, ClinicDashboard, DashboardPeriod } from "../../types/outreach-program.types"
import { ProgramStatusBadge } from "../program/ProgramControls"
import { CapUsagePanel, LastTick, NotAvailable } from "../program/ProgramPanels"

type PeriodKey = "today" | "week"

function Row({ label, value, detail }: { label: string; value: number | null | undefined; detail?: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-t py-1 first:border-t-0">
      <span className="text-sm text-muted-foreground">
        {label}
        {detail && <span className="block text-[11px]">{detail}</span>}
      </span>
      <span className="text-sm font-semibold tabular-nums">{value ?? "-"}</span>
    </div>
  )
}

const byReason = (b: ByReason | null | undefined) => describeCounts(b?.by_reason) || undefined

function PeriodView({ period, label }: { period: DashboardPeriod | null | undefined; label: string }) {
  if (!period) return <NotAvailable what={label} />
  const { calls, consents, texts } = period
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-3" data-testid={`period-${label}`}>
      <div>
        <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Calls</h4>
        {!calls ? (
          <NotAvailable what="Calls" />
        ) : (
          <>
            <Row label="Booked" value={calls.booked} />
            <Row label="Dialed" value={calls.dialed} />
            <Row label="Answered" value={calls.answered} />
            <Row label="Voicemail" value={calls.voicemail} />
            <Row label="Failed" value={calls.failed?.total} detail={byReason(calls.failed)} />
            <Row label="Skipped" value={calls.skipped?.total} detail={byReason(calls.skipped)} />
            <Row label="Cancelled" value={calls.cancelled} />
            <Row label="Missed" value={calls.missed} />
          </>
        )}
      </div>
      <div>
        <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Outcomes</h4>
        <Row label="Successful check-ins" value={period.checkins_successful} />
        {!consents ? (
          <NotAvailable what="Consents" />
        ) : (
          <>
            <Row label="Consented" value={consents.consented?.total} />
            <Row label="· by phone" value={consents.consented?.phone} />
            <Row label="· online" value={consents.consented?.online} />
            <Row label="· by staff" value={consents.consented?.staff} />
            <Row label="· other" value={consents.consented?.other} />
            <Row label="Declined" value={consents.declined} />
            <Row label="Disenrolled" value={consents.disenrolled?.total} detail={byReason(consents.disenrolled)} />
          </>
        )}
      </div>
      <div>
        <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Texts</h4>
        {!texts ? (
          <NotAvailable what="Texts" />
        ) : (
          <>
            <Row label="Sent" value={texts.sent} />
            <Row label="Delivered" value={texts.delivered} />
            <Row label="Replied" value={texts.replied} />
            <Row label="STOP" value={texts.stop} />
          </>
        )}
      </div>
    </div>
  )
}

/** Today and this week (Central) for one clinic: GET /outreach/org/{org}/dashboard. */
export function CallsDashboardTab({ orgId }: { orgId: string }) {
  const [data, setData] = useState<ClinicDashboard | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [period, setPeriod] = useState<PeriodKey>("today")

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setData(await programApi.getClinicDashboard(orgId))
      setError(null)
    } catch (err) {
      setError(apiErrorMessage(err, "failed"))
    } finally {
      setLoading(false)
    }
  }, [orgId])

  useEffect(() => {
    load()
  }, [load])

  if (!data) {
    return (
      <div className="py-12 text-center text-sm text-muted-foreground">
        {loading ? (
          <Loader2 className="mx-auto h-5 w-5 animate-spin" />
        ) : (
          <>
            <p>Calls dashboard: not available{error ? ` (${error})` : ""}.</p>
            <Button variant="outline" size="sm" className="mt-3" onClick={load}>
              Retry
            </Button>
          </>
        )}
      </div>
    )
  }

  const current = data.periods?.[period]
  const program = data.program
  const inboxLink = data.inbox?.link || `/outreach/inbox?org=${encodeURIComponent(orgId)}`

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="inline-flex rounded-md border p-0.5" role="group" aria-label="Period">
          {(["today", "week"] as PeriodKey[]).map((k) => (
            <button
              key={k}
              type="button"
              aria-pressed={period === k}
              onClick={() => setPeriod(k)}
              className={
                period === k
                  ? "rounded px-3 py-1 text-xs font-medium bg-primary text-primary-foreground"
                  : "rounded px-3 py-1 text-xs text-muted-foreground"
              }
            >
              {k === "today" ? "Today" : "This week"}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {current && (
            <span>
              {formatCentral(current.start)} – {formatCentral(current.end)} Central
            </span>
          )}
          <Button variant="ghost" size="sm" onClick={load} disabled={loading} aria-label="Reload dashboard">
            {loading ? <Loader2 className="animate-spin" /> : <RefreshCw />}
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="pt-5">
          <PeriodView period={current} label={period === "today" ? "Today" : "This week"} />
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Upcoming calls</CardTitle>
            <CardDescription>Booked calls by day, today and the next 6 days (Central).</CardDescription>
          </CardHeader>
          <CardContent>
            {!data.upcoming_calls ? (
              <NotAvailable what="Upcoming calls" />
            ) : data.upcoming_calls.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nothing booked.</p>
            ) : (
              <table className="w-full text-sm" data-testid="upcoming-calls">
                <thead>
                  <tr className="text-left text-xs text-muted-foreground">
                    <th className="py-1 font-normal">Day</th>
                    <th className="py-1 text-right font-normal">Consent</th>
                    <th className="py-1 text-right font-normal">Check-in</th>
                    <th className="py-1 text-right font-normal">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {data.upcoming_calls.map((d) => (
                    <tr key={d.date} className="border-t">
                      <td className="py-1">{d.date}</td>
                      <td className="py-1 text-right tabular-nums">{d.consent}</td>
                      <td className="py-1 text-right tabular-nums">{d.checkin}</td>
                      <td className="py-1 text-right tabular-nums font-medium">{d.total}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              Program {program && <ProgramStatusBadge program={program} />}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {!program ? (
              <NotAvailable what="Program status" />
            ) : (
              <>
                {program.status === "paused" && program.pause?.reason && (
                  <p className="text-sm text-warning">Paused: {program.pause.reason}</p>
                )}
                <LastTick tick={program.last_tick} />
                <CapUsagePanel usage={program.cap_usage} />
                {data.shadow_plan && (
                  <p className="text-xs text-muted-foreground">
                    {data.shadow_plan.count} plan entr{data.shadow_plan.count === 1 ? "y" : "ies"} for{" "}
                    {data.shadow_plan.plan_date} (nothing booked). See the Clinic program tab for the list.
                  </p>
                )}
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Reminder A/B</CardTitle>
          </CardHeader>
          <CardContent>{data.ab_arms ? <ArmResultsTable results={data.ab_arms} /> : <NotAvailable what="A/B results" />}</CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Auditor findings</CardTitle>
            {data.audit?.last_run_at && (
              <CardDescription>Last run {formatCentral(data.audit.last_run_at)}</CardDescription>
            )}
          </CardHeader>
          <CardContent>
            {!data.audit ? (
              <NotAvailable what="Auditor findings" />
            ) : data.audit.findings.length === 0 ? (
              <p className="text-sm text-success">No findings.</p>
            ) : (
              <ul className="space-y-1 text-sm" data-testid="audit-findings">
                {data.audit.findings.map((f) => (
                  <li key={f.invariant} className="flex items-baseline justify-between gap-3">
                    <span>
                      <span
                        className={
                          f.severity === "critical" || f.severity === "high" || f.severity === "error"
                            ? "mr-1.5 text-[10px] font-semibold uppercase text-destructive"
                            : "mr-1.5 text-[10px] font-semibold uppercase text-warning"
                        }
                      >
                        {f.severity}
                      </span>
                      {f.invariant.replace(/_/g, " ")}
                    </span>
                    <span className="tabular-nums font-medium">{f.count}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="flex flex-wrap items-center justify-between gap-3 pt-5">
          <p className="text-sm">
            {data.inbox ? (
              <>
                <span className="font-semibold tabular-nums">{data.inbox.open}</span> open item
                {data.inbox.open === 1 ? "" : "s"} in the outreach inbox
              </>
            ) : (
              "Outreach inbox count: not available"
            )}
          </p>
          <Button asChild variant="outline" size="sm">
            <Link to={inboxLink}>Open inbox</Link>
          </Button>
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground">Generated {formatCentral(data.generated_at)} Central.</p>
    </div>
  )
}
